import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import sanitizeHtml from 'sanitize-html';
import { useI18N } from '../../context/I18NContext';
import useWidgetStore from '../../store/useWidgetStore';
export default function WaitinglistList(props) {
    const { waitinglists, onRemove, removing } = props;
    const { t, ts } = useI18N();
    const theme = useTheme();
    const { language } = useWidgetStore();
    // the waiting list the consumer asked to be signed off, until they confirm it or think better
    // of it. The whole entry rather than its id, so the dialog can name what it is about.
    const [confirming, setConfirming] = useState(null);
    const date = (value) => dayjs(value).locale(language).format('D MMMM YYYY');
    /**
     * The rules come from the backend as the text it prints on /consumer/waitinglist/list, and
     * that text carries markup: it emphasises the values the consumer is waiting for. So it is
     * rendered as HTML rather than escaped, through the same sanitizer ObjectListPicker uses for
     * descriptions, and with only the tags that wording actually uses.
     */
    const rules = (value) => sanitizeHtml(value, {
        allowedTags: ['em', 'strong', 'center']
    });
    /** When the consumer was last told a place had come free, and how often that has happened. */
    const notified = (waitinglist) => waitinglist.lastNotified
        ? ts('Laatst bericht op %date% (%count%x)', {
            date: date(waitinglist.lastNotified),
            count: waitinglist.notifications
        })
        : t('Je hebt hierover nog geen bericht gehad.');
    const confirm = () => {
        if (confirming) {
            onRemove(confirming.id);
        }
        setConfirming(null);
    };
    if (!waitinglists.length) {
        return (_jsx(Typography, { variant: "body1", sx: { color: theme.palette.text.primary }, children: t('Je hebt je op geen wachtlijst aangemeld.') }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(Stack, { spacing: 1, sx: { marginBottom: 2 }, children: waitinglists.map((waitinglist) => (_jsxs(Box, { sx: {
                        background: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.divider,
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderRadius: 1,
                        padding: 1
                    }, children: [_jsx(Typography, { variant: "subtitle2", component: "div", sx: { fontWeight: 700, fontSize: '1.1rem' }, children: _jsx("span", { dangerouslySetInnerHTML: { __html: rules(waitinglist.rules) } }) }), waitinglist.date && (_jsx(Typography, { variant: "body2", children: ts('Aangemeld op %date%', { date: date(waitinglist.date) }) })), _jsx(Typography, { variant: "body2", children: notified(waitinglist) }), waitinglist.available && (
                        // a place is free right now: booking it is not wired up yet, so this
                        // says so rather than offering a button that does nothing
                        _jsx(Typography, { variant: "body2", color: "primary", children: ts('Er is nu plek op %date% om %time%', {
                                date: date(waitinglist.available.date),
                                time: waitinglist.available.startTime
                            }) })), _jsx(Button, { fullWidth: true, size: "small", variant: "contained", sx: { marginTop: 1, paddingY: '2px' }, onClick: () => setConfirming(waitinglist), children: t('Afmelden') })] }, waitinglist.id))) }), _jsxs(Dialog, { open: !!confirming, onClose: () => setConfirming(null), fullWidth: true, maxWidth: "xs", children: [_jsx(DialogTitle, { sx: { fontSize: '1.25rem', fontWeight: 700 }, children: t('Afmelden voor de wachtlijst') }), _jsxs(DialogContent, { children: [_jsx(Typography, { variant: "body1", children: t('Weet je zeker dat je je wilt afmelden voor deze wachtlijst?') }), confirming && (_jsx(Typography, { variant: "body2", sx: { marginTop: 1, fontWeight: 700 }, children: _jsx("span", { dangerouslySetInnerHTML: { __html: rules(confirming.rules) } }) }))] }), _jsxs(DialogActions, { children: [_jsx(Button, { variant: "outlined", disabled: removing, onClick: () => setConfirming(null), children: t('Annuleren') }), _jsx(Button, { variant: "contained", disabled: removing, onClick: confirm, children: t('Afmelden') })] })] })] }));
}
